import type { CalendarEvent } from "@tellescope/types-models";

const isWithinMinutes = (startTimeInMS: number, minutes?: number): boolean =>
  minutes === undefined ? false : startTimeInMS - Date.now() - minutes * 60 * 1000 < 0;

export interface RescheduleGate {
  visible: boolean;
  disabled: boolean;
}

/**
 * Mirrors packages/private/portal/src/pages/Events.tsx's reschedule gate (legacy portal), minus
 * its PortalCustomization.hideReschedule check — v2 gates that at the block level via
 * `allowReschedule` instead, since v2 has no PortalCustomization fetch today. Keep the shared
 * conditions in sync with legacy if either changes.
 *
 * `isUpcoming`/`isCancelled` (from `useEventStatus`) are required rather than left for callers to
 * AND in separately, so `visible` is a complete answer on its own for any future call site.
 */
export const getRescheduleGate = (
  event: CalendarEvent,
  allowReschedule: boolean | undefined,
  isUpcoming: boolean,
  isCancelled: boolean
): RescheduleGate => {
  const isSoloOrEligibleGroup =
    !event.enduserAttendeeLimit || event.enduserAttendeeLimit === 1 || !!event.allowGroupReschedule;

  const visible = !!allowReschedule && !!event.bookingPageId && isSoloOrEligibleGroup && isUpcoming && !isCancelled;

  const disabled =
    !!event.rescheduledAt ||
    isWithinMinutes(event.startTimeInMS, event.preventRescheduleMinutesInAdvance) ||
    event.preventRescheduleInPortal === true;

  return { visible, disabled };
};

export interface CancelGate {
  visible: boolean;
  disabled: boolean;
}

/**
 * Visible/disabled split for the in-app Cancel action, mirroring getRescheduleGate's shape.
 * `disabled` is true once already rescheduled — cancelling makes no sense once superseded by a
 * new event — as well as for the existing preventCancelInPortal / preventCancelMinutesInAdvance
 * conditions, which previously had no home in a shared gate function.
 */
export const getCancelGate = (
  event: CalendarEvent,
  allowCancel: boolean | undefined,
  isUpcoming: boolean,
  isCancelled: boolean
): CancelGate => {
  const visible = !!allowCancel && isUpcoming && !isCancelled;

  const disabled =
    !!event.rescheduledAt ||
    isWithinMinutes(event.startTimeInMS, event.preventCancelMinutesInAdvance) ||
    event.preventCancelInPortal === true;

  return { visible, disabled };
};
